import { useRef, useState, type DragEvent as ReactDragEvent } from 'react';

import { LOGO_ACCEPT_MIME, LOGO_SPEC_HINT, validateLogoFile } from './copilot/copilotInputCard';
import { detectLogoColours, type DetectedColours } from '../lib/logoColours';
import { supabase } from '../lib/supabase';

/**
 * Drag-drop (or click) upload for a house's logo.
 *
 * 🔴 ONE UPLOADER, TWO PLACES. This was written inside `CopilotChat` as
 * `LogoDropzone` and served onboarding only. When Admin → Branding gained the
 * ability to CHANGE a logo (2026-09-14) the obvious move was a second dropzone
 * on that page, and that is precisely the mechanical divergence CLAUDE.md
 * forbids: two uploaders means two answers to what file types are allowed, two
 * size ceilings, and two chances for one of them to drift off the Edge
 * Function's actual limits. Extracted here instead; both callers render this.
 *
 * The limits themselves are not restated — `validateLogoFile` and
 * `LOGO_ACCEPT_MIME` live in `copilotInputCard.ts` and are the only statement of
 * them on the client. The Edge Function re-checks both server-side regardless,
 * because a client-side gate is a courtesy and not a control.
 *
 * ⚠️ RAW BYTES, DELIBERATELY. The file is posted as-is rather than through the
 * photo compressor: that re-encodes to JPEG, which destroys PNG transparency and
 * rasterises an SVG. A logo needs its fidelity in a way a lot photograph does
 * not.
 */

export interface LogoUploaderProps {
  /** The current logo URL, or null/blank when the house has none. */
  logoUrl: string | null;
  /** Called with the new public URL once an upload succeeds. */
  onLogoUrl: (url: string) => void;
  /**
   * Reports upload-in-flight upward, so the parent can block its own Save.
   * A logo that is still uploading has no URL yet, and saving mid-flight
   * would write the OLD url over the one about to arrive.
   */
  onUploadingChange?: (uploading: boolean) => void;
  /** The parent is busy for its own reasons (saving, loading). */
  busy?: boolean;
  /**
   * Which house to upload for.
   *
   * 🔴 REQUIRED WHEREVER THE USER OWNS MORE THAN ONE. The Edge Function resolves
   * the caller's owned tenants and, for an owner of several, REFUSES to guess —
   * so Admin → Branding must pass the house its selector is pointed at. The
   * onboarding copilot omits it because a house being onboarded is the only one
   * its owner has.
   */
  tenantId?: string | null;
  /** Field label. */
  label?: string;
  /**
   * The colours read out of the logo the owner just picked, offered as
   * suggestions.
   *
   * 🔴 SUGGESTIONS, NOT ASSIGNMENTS. Jim: *"They can specify different ones
   * after that."* The caller decides what to do with these and MUST NOT
   * overwrite a colour the owner has already typed — a logo upload silently
   * rewriting a hex somebody chose on purpose is worse than never detecting
   * anything.
   *
   * Called at most once per picked file, and not at all when the logo is
   * monochrome or undecodable.
   */
  onColoursDetected?: (colours: DetectedColours) => void;
}

export function LogoUploader({
  logoUrl,
  onLogoUrl,
  onUploadingChange,
  busy = false,
  tenantId = null,
  label = 'Logo (optional)',
  onColoursDetected,
}: LogoUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const currentUrl = typeof logoUrl === 'string' && logoUrl.trim() !== '' ? logoUrl.trim() : null;
  const disabled = busy || uploading;

  /**
   * The Edge Function's own explanation, when it sent one.
   *
   * Returns null whenever there is nothing meaningful to show, so the caller can
   * fall back — a missing `context`, an unreadable body, or a response that
   * carries an error CODE but no sentence for a human.
   */
  async function refusalMessage(fnErr: unknown): Promise<string | null> {
    const context = (fnErr as { context?: unknown } | null)?.context;
    if (!(context instanceof Response)) return null;
    try {
      const body = (await context.clone().json()) as { message?: unknown };
      return typeof body.message === 'string' && body.message.trim() !== '' ? body.message : null;
    } catch {
      return null;
    }
  }

  async function handleFile(file: File): Promise<void> {
    setError(null);
    const invalid = validateLogoFile(file);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    // Read the colours from the LOCAL file, before the upload and independent of
    // it: the detection is a convenience and must never delay or fail the thing
    // the owner actually asked for. `detectLogoColours` never throws.
    if (onColoursDetected) {
      void detectLogoColours(file).then((colours) => {
        if (colours !== null) onColoursDetected(colours);
      });
    }

    setUploading(true);
    onUploadingChange?.(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (tenantId !== null && tenantId !== '') fd.append('tenant_id', tenantId);
      const { data, error: fnErr } = (await supabase.functions.invoke('upload-tenant-logo', {
        body: fd,
      })) as {
        data: { public_url?: string } | null;
        error: { message?: string } | null;
      };
      // 🔴 supabase-js surfaces a non-2xx as `error` with `data` null — the EF's
      // JSON body is NOT in `data`. It rides on the error's `context`, which is
      // the raw Response, and reading it is the ONLY way the house sees why
      // their logo was refused.
      //
      // Without this the shape gate is invisible: the Edge Function composes a
      // sentence explaining that their lockup is 17× wider than it is tall and
      // would render 12pt high, and the owner reads "Upload failed. Please try
      // again." — then uploads the same file again, because nothing told them
      // not to.
      //
      // Guarded end to end: `context` is absent on a network failure, and a
      // body that is not JSON (a gateway's HTML error page) throws. Either way
      // we fall back to the generic line rather than showing a stack trace.
      if (fnErr || !data?.public_url) {
        setError((await refusalMessage(fnErr)) ?? 'Upload failed. Please try again.');
        return;
      }
      onLogoUrl(data.public_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed. Try again.');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  }

  function onDrop(e: ReactDragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {/* The spec, BEFORE they pick a file rather than after we refuse one.
          Telling a house what to send is the cheap half of the gate; the Edge
          Function enforces it and explains any refusal in its own words. */}
      <span
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          opacity: 0.85,
          lineHeight: 1.4,
        }}
      >
        {LOGO_SPEC_HINT}
      </span>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload a logo image"
        aria-disabled={disabled}
        data-testid="logo-uploader"
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          padding: '0.625rem 0.75rem',
          border: `1px dashed ${dragOver ? 'var(--system-brand-accent)' : 'var(--border)'}`,
          borderRadius: '0.5rem',
          background: dragOver ? 'var(--surface-muted, #f1f5f9)' : 'var(--surface)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled && !uploading ? 0.6 : 1,
        }}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Logo preview"
            style={{
              width: '2.5rem',
              height: '2.5rem',
              objectFit: 'contain',
              borderRadius: '0.375rem',
              background: 'var(--surface-muted, #f1f5f9)',
              flexShrink: 0,
            }}
          />
        ) : (
          <span
            aria-hidden="true"
            style={{
              width: '2.5rem',
              height: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.375rem',
              background: 'var(--surface-muted, #f1f5f9)',
              fontSize: '1.125rem',
              flexShrink: 0,
            }}
          >
            🖼️
          </span>
        )}
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
          {uploading
            ? 'Uploading…'
            : currentUrl
              ? 'Logo added — drop or click to replace'
              : 'Drop an image here, or click to choose'}
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={LOGO_ACCEPT_MIME.join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      {error && (
        <span role="alert" style={{ fontSize: '0.75rem', color: 'var(--danger-text, #991b1b)' }}>
          {error}
        </span>
      )}
    </div>
  );
}
